import styled, { DefaultTheme, withTheme } from 'styled-components';
import Image from 'next/legacy/image';
import { Col, Row } from 'antd';

import useI18n from '../../common/hooks/useI18n';
import { IContact } from '../../common/model/mailchimp/contact.model';

import ContactButton from './contactButton';

type Props = {
  query: string;
  index: number;
  onContactButtonClick: (contact: IContact) => void;
  sticky?: boolean;
  theme: DefaultTheme;
};

const Background = styled.div<{ $sticky: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  padding-top: ${(props) => (props.$sticky ? '16px' : '64px')};
  padding-bottom: ${(props) => (props.$sticky ? '16px' : '64px')};
  padding-left: ${(props) =>
    props.theme.grid.getGridColumns(props.$sticky ? 1 : 3, 1)};
  padding-right: ${(props) =>
    props.theme.grid.getGridColumns(props.$sticky ? 1 : 3, 1)};

  @media ${(props) => props.theme.breakpoints.smd} {
    padding-top: ${(props) => (props.$sticky ? '16px' : '32px')};
    padding-bottom: ${(props) => (props.$sticky ? '16px' : '32px')};
    padding-left: ${(props) => props.theme.grid.getGridColumns(1)};
    padding-right: ${(props) => props.theme.grid.getGridColumns(1)};
  }
`;

const ImageAbsoluteContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
`;

const ImageContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const Content = styled.div`
  text-align: center;
`;

const Title = styled.h2<{ $sticky: boolean }>`
  ${(props) => (props.$sticky ? props.theme.font.p1 : props.theme.font.h2)}
  font-size: ${(props) => (props.$sticky ? '16px !important' : 'unset')};
  font-weight: ${(props) => (props.$sticky ? '500 !important' : 'unset')};
  max-width: ${(props) => (props.$sticky ? '100%' : 'unset')};
  white-space: ${(props) => (props.$sticky ? 'nowrap' : 'unset')};
  overflow: ${(props) => (props.$sticky ? 'hidden' : 'unset')};
  text-overflow: ${(props) => (props.$sticky ? 'ellipsis' : 'unset')};
  color: white;
`;

const Subtitle = styled.p`
  margin-top: 32px;
  margin-bottom: 32px;
  ${(props) => props.theme.font.p1}
  color: white;
`;

const ArrowContainer = styled.div`
  margin-bottom: 24px;

  @media ${(props) => props.theme.breakpoints.xs} {
    display: none;
  }
`;

const HighlightedBanner: React.FC<Props> = ({
  query,
  index,
  onContactButtonClick,
  sticky = false,
  theme,
}) => {
  const i18n = useI18n();

  return (
    <Background as="div" $sticky={sticky}>
      <ImageAbsoluteContainer>
        <ImageContainer>
          <Image
            alt="sellHouseHighlightedBanner"
            layout="fill"
            objectFit="cover"
            objectPosition="center top"
            src="/images/sell_house/highlighted_banner.png"
            sizes={theme.getImageSizes('100vw')}
          />
        </ImageContainer>
      </ImageAbsoluteContainer>
      <Content>
        <Row align="middle">
          <Col xs={sticky ? 0 : 24} md={sticky ? 18 : 24}>
            <Title as="h2" $sticky={sticky}>
              {i18n.t(
                `dynamic.sellHousePage.${query}.highlightedBannerTitle_${
                  index + 1
                }`
              )}
            </Title>
          </Col>
          <Col span={sticky ? 0 : 24}>
            <Subtitle>
              {i18n.t(
                `dynamic.sellHousePage.${query}.highlightedBannerDescription`
              )}
            </Subtitle>
          </Col>
          <Col xs={24} md={sticky ? 6 : 24}>
            {!sticky && (
              <ArrowContainer>
                <Image
                  alt="arrows"
                  width={40}
                  height={40}
                  style={{ filter: 'brightness(0) invert(1)' }}
                  src={`/images/sell_house/arrows.svg`}
                />
              </ArrowContainer>
            )}
            <ContactButton
              query={query}
              onButtonClick={onContactButtonClick}
              type="default"
              size={sticky ? 'middle' : undefined}
            />
          </Col>
        </Row>
      </Content>
    </Background>
  );
};

export default withTheme(HighlightedBanner);
